import SingleSelectDropdown from '@components/shared/filters/single-select-dropdown/SingleSelectDropdown';
import { AppState } from '@store/AppState';
import { useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { PortfolioFilterType } from '../../../../../enums/PortfolioFilterType';
import { useCheckDevice } from '../../../../../hooks/UseCheckDevice';
import { usePortfolioFilter } from '../../../../../hooks/UsePortfolioFilter';
import type { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import type { DealerState } from '../../../../../types/DealerState';
import { defaultRange } from '../../../../../types/PortfolioFilterResource';
import DatePickerRange from '../../../../shared/filters/date-picker-range/DatePickerRange';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import OptOutPreferenceDropdown from '../../portfolio-communication-filter/OptOutPreferenceDropdown';
import PortfolioRecallFilter from '../../portfolio-recall-filter/PortfolioRecallFilter';

type BuyerDetailsOptionsProps = {
  onOpen: () => void;
  onClose: () => void;
};

export const BuyerDetailsOptions: FC<BuyerDetailsOptionsProps> = ({ onOpen, onClose }) => {
  const { getPortfolioView, getSelectionToggleType, setSelectedFilter } = usePortfolioFilter();
  const { dealType } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const currentView = getPortfolioView(dealType);
  const { isMobileDevice } = useCheckDevice();
  const { featureToggle, storeServices } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );

  const addExpiringTextToDates = useMemo(
    () =>
      currentView.filters.campaignExpiryDates.map((item) => ({
        ...item,
        label: `Expiring ${item.label.slice(0, 7)}${item.label.slice(9)}`,
      })),
    [currentView.filters.campaignExpiryDates],
  );

  const addCampaignViewsTextToCount = useMemo(() => {
    return currentView.filters.campaignActivityViewCounts.map((item) => ({
      ...item,
      label: item.label === '1' ? item.label + ' Campaign View' : item.label + ' Campaign Views',
    }));
  }, [currentView.filters.campaignActivityViewCounts]);

  const inventivesList = useMemo(() => {
    return currentView.filters.incentives.filter((item) => {
      if (!featureToggle?.fordPrivateIncentive && item.key === 'FMC Private Offer') {
        return false;
      }
      return true;
    });
  }, [currentView.filters.incentives, featureToggle?.fordPrivateIncentive]);

  return (
    <>
      <MultiSelectDropdown
        placeholder='Sold By'
        allowSearchFilter
        enableCheckAll
        values={currentView.selectedFilters.soldBy}
        onChange={(value) => setSelectedFilter(PortfolioFilterType.SoldBy, value, dealType)}
        onUpdate={(value) => setSelectedFilter(PortfolioFilterType.SoldBy, value, dealType, false)}
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.soldBy}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.SoldBy,
          dealType,
          currentView?.selectedFilters.soldBy,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='buyer-details-sold-by-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Incentives'
        values={currentView.selectedFilters.incentives}
        onChange={(value) => setSelectedFilter(PortfolioFilterType.Incentives, value, dealType)}
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.Incentives, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={inventivesList}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.Incentives,
          dealType,
          currentView?.selectedFilters.incentives,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='buyer-details-incentives-dropdown'
      />
      {featureToggle?.fordPrivateIncentive &&
        currentView.filters?.fordIncentiveNames?.length > 0 && (
          <MultiSelectDropdown
            placeholder='FMC Private Offer'
            values={currentView.selectedFilters.fordIncentiveNames}
            onChange={(value) =>
              setSelectedFilter(PortfolioFilterType.FordIncentiveNames, value, dealType)
            }
            onUpdate={(value) =>
              setSelectedFilter(PortfolioFilterType.FordIncentiveNames, value, dealType, false)
            }
            displaySelectedLabels
            selectedLabelPosition='right'
            type='am-dropdown'
            allowSearchFilter
            options={currentView.filters.fordIncentiveNames}
            onOpen={onOpen}
            onClose={onClose}
            selectionToggleType={getSelectionToggleType(
              PortfolioFilterType.FordIncentiveNames,
              dealType,
              currentView?.selectedFilters.fordIncentiveNames,
            )}
            cssClass={isMobileDevice ? 'responsive-adjust' : ''}
            dataTestId='buyer-details-fmc-incentives-dropdown'
          />
        )}
      {currentView.filters.campaignExpiryDates.length > 0 && (
        <MultiSelectDropdown
          placeholder='Private Incentives'
          allowSearchFilter
          enableCheckAll
          values={currentView.selectedFilters.campaignExpiryDates}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.CampaignExpiryDates, value, dealType)
          }
          onUpdate={(value) =>
            setSelectedFilter(PortfolioFilterType.CampaignExpiryDates, value, dealType, false)
          }
          displaySelectedLabels
          selectedLabelPosition='right'
          type='am-dropdown'
          options={addExpiringTextToDates}
          onOpen={onOpen}
          onClose={onClose}
          selectionToggleType={getSelectionToggleType(
            PortfolioFilterType.CampaignExpiryDates,
            dealType,
            currentView?.selectedFilters.campaignExpiryDates,
          )}
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
          dataTestId='buyer-details-campaign-expiry-dates-dropdown'
        />
      )}
      <DatePickerRange
        placeholder='Last Service Date'
        selectedValue={currentView?.selectedFilters.lastServiceDate}
        possibleRange={defaultRange}
        onOpen={onOpen}
        onClose={onClose}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.LastServiceDate, value, dealType)
        }
        type='am-dropdown'
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
      <MultiSelectDropdown
        placeholder='Campaign Activity'
        values={currentView.selectedFilters.campaignActivityViewCounts}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CampaignActivityViewCounts, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CampaignActivityViewCounts, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={addCampaignViewsTextToCount}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.CampaignActivityViewCounts,
          dealType,
          currentView?.selectedFilters.campaignActivityViewCounts,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='buyer-details-campaign-activity-dropdown'
      />
      {featureToggle?.websiteShopperActivity && (
        <SingleSelectDropdown
          placeholder='Website Activity'
          options={currentView.filters.shopperActivity}
          value={currentView.selectedFilters.shopperActivity}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.ShopperActivity, value, dealType)
          }
          onUpdate={(value) =>
            setSelectedFilter(PortfolioFilterType.ShopperActivity, value, dealType, false)
          }
          onOpen={onOpen}
          onClose={onClose}
          selectedLabelPosition='right'
          type='am-dropdown'
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
      {storeServices.hasRecall && featureToggle.recall && (
        <PortfolioRecallFilter
          options={currentView.filters.recall}
          onChange={(value) => setSelectedFilter(PortfolioFilterType.Recall, value, dealType)}
          onUpdate={(value) =>
            setSelectedFilter(PortfolioFilterType.Recall, value, dealType, false)
          }
          values={currentView.selectedFilters.recall}
          onOpen={onOpen}
          onClose={onClose}
          isMobileDevice={isMobileDevice}
        />
      )}
      <OptOutPreferenceDropdown
        onOpen={onOpen}
        onClose={onClose}
        options={currentView.filters.optOutPreference}
        values={currentView.selectedFilters.optOutPreference}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.OptOutPreference, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.OptOutPreference, value, dealType, false)
        }
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
    </>
  );
};
